/**
 * Top-level routing + auth gate.
 *
 * URL shapes recognised:
 *   /                  → ProjectPicker (logged in: list from API; logged out: login form)
 *   /p/<projectId>     → LoginGate → ProjectViewer (fetch metadata + signed URL)
 *   /?dev=<tilesetUrl> → Direct tileset URL, still requires login (no API metadata fetch)
 */
import { useEffect, useState } from 'react'
import { LoginGate } from './auth/LoginGate'
import { ProjectViewer } from './ui/ProjectViewer'
import { ProjectPicker } from './ui/ProjectPicker'
import { Scene } from './viewer/Scene'

interface Route {
  kind: 'picker' | 'project' | 'dev'
  projectId?: string
  tilesetUrl?: string
}

function parseRoute(): Route {
  const url = new URL(window.location.href)
  const devUrl = url.searchParams.get('dev')
  if (devUrl) {
    return { kind: 'dev', tilesetUrl: devUrl }
  }
  const m = url.pathname.match(/^\/p\/([A-Za-z0-9_-]+)\/?$/)
  if (m) {
    return { kind: 'project', projectId: m[1] }
  }
  return { kind: 'picker' }
}

export function App() {
  const [route, setRoute] = useState<Route>(parseRoute)

  useEffect(() => {
    const onPopState = () => setRoute(parseRoute())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  if (route.kind === 'dev' && route.tilesetUrl) {
    return (
      <LoginGate>
        <Scene tilesetUrl={route.tilesetUrl} />
      </LoginGate>
    )
  }

  if (route.kind === 'project' && route.projectId) {
    return (
      <LoginGate>
        <ProjectViewer projectId={route.projectId} />
      </LoginGate>
    )
  }

  return (
    <LoginGate>
      <ProjectPicker />
    </LoginGate>
  )
}
